// The CPU half of an instance buffer, written once for both backends.
//
// The three batches write floats into `data` and call `reserve`. The array
// and its growth name no graphics API, so `gl/instances.ts` and
// `gpu/instances.ts` extend this class and add the GPU buffer and the draw.
// `rebuild` runs on a growth alone, so a reserve costs what it cost before.

/** The one primitive every batch draws: a four-corner strip (R553). */
export const STRIP_VERTICES = 4;

export abstract class InstanceArray {
  data: Float32Array;
  /** How many instances the caller wrote since `begin()`. */
  count = 0;
  capacity: number;

  constructor(readonly floatsPerInstance: number, capacity: number) {
    this.capacity = Math.max(1, capacity);
    this.data = new Float32Array(this.capacity * floatsPerInstance);
  }

  begin(): void { this.count = 0; }

  /**
   * Reserves one instance and returns its first float index, doubling the
   * array when it is full. The growth copies the written instances only.
   */
  reserve(): number {
    if (this.count >= this.capacity) this.grow();
    const at = this.count * this.floatsPerInstance;
    this.count++;
    return at;
  }

  private grow(): void {
    this.capacity *= 2;
    const next = new Float32Array(this.capacity * this.floatsPerInstance);
    next.set(this.data);
    this.data = next;
    this.rebuild();
  }

  /** Builds the GPU buffer again at the new size, where the device is live. */
  protected abstract rebuild(): void;
}
